// ── ingredientVariance.js — 이론 식재료 사용량 vs 실제 매입 비교 ──
// 메뉴 판매수량 × 레시피 = 이론 사용량 (기대값)
// 영수증 lineItems 합산 = 실제 매입량 (관측값)
// 편차 = 실제 매입 - 이론 사용량 (+ 재고 변화량 옵션)
//
// 단위 처리:
//   - 단가표 prices에 unit, net_g, price 있음
//   - 레시피 ingredient.qty는 단가표 단위와 같다고 가정
//     (즉, 단가표 unit='g', net_g=1이면 ingredient.qty=100 → 100g)
//     (단가표 unit='kg', net_g=1000이면 ingredient.qty=0.5 → 0.5kg = 500g)
//   - 영수증 lineItem.qty는 구매 단위 (보통 박스, 봉지, 개)
//     → 단가표 단위로 변환: actual_in_unit = lineItem.qty × (lineItem_net_g / price.net_g)
//     단순화: 영수증과 단가표 net_g가 같다고 가정 (서로 매칭된 식재료라면)
//
// 매칭은 ingredientMatch.normalizeIngredientName 사용 (공백·대소문자 무시)

import { findPriceByName, normalizeIngredientName } from './ingredientMatch';
import { aggregateMenuSales, isMenuMatch, normalizeMenuName } from './menuProfitability';
import { usableG } from './priceYield';   // V152 실사용량 (신선식품 로스 제외)

const num = (v) => Number(v) || 0;

// ── 반제품 재귀 분해 (OrderSheet와 동일 패턴) ──
// type='semi'인 레시피면 그 ingredients로 재귀 분해
// 분해 비율: qty / total_output
function decomposeIngredient(name, qty, allRecipes, depth = 0) {
  if (depth > 10) return [{ name, qty }];  // 무한루프 방지
  const semi = allRecipes.find(r => r.name === name && r.type === 'semi');
  if (semi && Number(semi.total_output) > 0 && Array.isArray(semi.ingredients)) {
    const ratio = qty / Number(semi.total_output);
    const out = [];
    for (const sub of semi.ingredients) {
      const subQty = (Number(sub.qty) || 0) * ratio;
      if (subQty <= 0) continue;
      out.push(...decomposeIngredient(sub.name, subQty, allRecipes, depth + 1));
    }
    return out;
  }
  return [{ name, qty }];
}


// ── 메뉴 판매수량 → 이론 식재료 소비량 ──
// salesAgg = { menuKey: { name, qty, revenue } } from aggregateMenuSales
// 반환: { ingredientNormName: { name, qty, cost, unit, dataQuality } }
export function calcTheoreticalUsage(salesAgg, categories, prices, menuRecipeMap) {
  const out = {};
  if (!salesAgg || !categories) return out;

  // 모든 레시피 모으기 (메뉴 + 반제품 — 반제품 분해용)
  const allRecipes = [];
  for (const cat of (categories || [])) {
    for (const item of (cat.items || [])) {
      allRecipes.push(item);
    }
  }
  const allMenus = allRecipes.filter(r => r.type === 'menu');

  // 디버그용: 메뉴 → 레시피 매핑 결과
  const mapping = [];

  for (const key of Object.keys(salesAgg)) {
    const sale = salesAgg[key];
    if (!sale || sale.qty <= 0) continue;

    // 매칭: 1) 수동 매핑(menuRecipeMap) 우선 → 2) fuzzy 매칭 fallback
    let matchedMenu = null;
    let matchSource = 'fuzzy';  // 'manual' | 'fuzzy' | 'excluded'
    const manualMapKey = sale.name;
    const manualValue = menuRecipeMap && menuRecipeMap[manualMapKey];
    if (manualValue === '__exclude__') {
      // 사용자가 명시적으로 제외
      mapping.push({ saleName: sale.name, saleQty: sale.qty, matchedRecipe: null, matchSource: 'excluded' });
      continue;
    } else if (manualValue) {
      // 수동 매핑 (recipe id 또는 name)
      matchedMenu = allMenus.find(m => m.id === manualValue) || allMenus.find(m => m.name === manualValue);
      if (matchedMenu) matchSource = 'manual';
    }
    if (!matchedMenu) {
      // fuzzy fallback
      for (const m of allMenus) {
        if (isMenuMatch(m.name, sale.name)) { matchedMenu = m; break; }
      }
    }
    if (!matchedMenu) {
      mapping.push({ saleName: sale.name, saleQty: sale.qty, matchedRecipe: null, matchSource: 'none' });
      continue;
    }
    mapping.push({
      saleName: sale.name,
      saleQty: sale.qty,
      matchedRecipe: matchedMenu.name,
      recipeId: matchedMenu.id,
      ingredientCount: (matchedMenu.ingredients || []).length,
      matchSource,
    });

    const totalOutput = num(matchedMenu.total_output);
    const servingG = num(matchedMenu.serving_g);
    // 1인분 비율 — 없으면 1로 처리 (qty가 1인분당 사용량으로 간주)
    const perServingRatio = (totalOutput > 0 && servingG > 0) ? (servingG / totalOutput) : 1;

    for (const ing of (matchedMenu.ingredients || [])) {
      const ingName = ing?.name;
      if (!ingName) continue;
      const ingQty = num(ing.qty);
      if (ingQty <= 0) continue;

      const perServing = ingQty * perServingRatio;
      const totalQtyForIng = perServing * sale.qty;

      // 반제품 분해 — 반제품이면 원재료로 재귀 분해, 아니면 [{name, qty: totalQtyForIng}] 반환
      const decomposed = decomposeIngredient(ingName, totalQtyForIng, allRecipes);

      for (const raw of decomposed) {
        const rawName = raw.name;
        const totalQtyGramsLike = raw.qty;
        if (!rawName || totalQtyGramsLike <= 0) continue;

        const norm = normalizeIngredientName(rawName);
        if (!out[norm]) {
          const price = findPriceByName(prices, rawName);
          const netG = price ? usableG(price) : 0;   // V152 실사용량 기준
          out[norm] = {
            name: rawName,
            qty: 0,
            cost: 0,
            unit: price?.unit || 'g',
            unitPrice: price ? num(price.price) : 0,
            netG,
            matched: !!price,
          };
        }
        // 단위 변환: 레시피 그램 → 단가표 가격 단위
        const netG = out[norm].netG;
        const divisor = netG > 0 ? netG : 1;
        const qtyInPriceUnit = totalQtyGramsLike / divisor;
        out[norm].qty += qtyInPriceUnit;
        out[norm].cost += qtyInPriceUnit * out[norm].unitPrice;
      }
    }
  }

  // 반올림
  for (const k of Object.keys(out)) {
    out[k].qty = Math.round(out[k].qty * 100) / 100;
    out[k].cost = Math.round(out[k].cost);
  }
  // mapping 정렬: qty 많은 순
  mapping.sort((a, b) => b.saleQty - a.saleQty);
  out.__menuMapping = mapping;
  return out;
}

// ── 영수증 lineItems → 실제 매입량 ──
// transactions: type=expense, lineItems 있음
// purchaseLog: items 또는 lineItems
// monthKey: 'YYYY-MM' (해당 월만 집계)
// 반환: { ingredientNormName: { name, qty, cost, unit, sources } }
export function calcActualPurchases(transactions, purchaseLog, monthKey, prices) {
  const out = {};
  const addItem = (rawName, qty, amount, srcLabel) => {
    if (!rawName) return;
    const norm = normalizeIngredientName(rawName);
    if (!norm) return;
    if (!out[norm]) {
      const price = findPriceByName(prices, rawName);
      out[norm] = {
        name: rawName,
        qty: 0,
        cost: 0,
        unit: price?.unit || '',
        matched: !!price,
        sources: [],
      };
    }
    out[norm].qty += num(qty);
    out[norm].cost += num(amount);
    if (srcLabel && !out[norm].sources.includes(srcLabel)) out[norm].sources.push(srcLabel);
  };

  // 거래장부 expense + lineItems
  for (const t of (transactions || [])) {
    if (t?.type !== 'expense') continue;
    if (!t?.date || !t.date.startsWith(monthKey)) continue;
    const items = Array.isArray(t.lineItems) ? t.lineItems : [];
    for (const li of items) {
      if (li?.isReturn || li?.kind === '반품') continue;
      if (li?.kind === '할인') continue;
      const amount = num(li.amount) || num(li.total) || (num(li.qty) * num(li.unitPrice));
      addItem(li.name, li.qty || li.quantity, amount, t.evidenceType || '거래장부');
    }
  }

  // 매입메모 purchaseLog + items
  for (const p of (purchaseLog || [])) {
    if (!p?.date || !p.date.startsWith(monthKey)) continue;
    const items = Array.isArray(p.items) ? p.items : (Array.isArray(p.lineItems) ? p.lineItems : []);
    for (const li of items) {
      if (li?.isReturn || li?.kind === '반품') continue;
      if (li?.kind === '할인') continue;
      const amount = num(li.amount) || num(li.total) || (num(li.qty) * num(li.unitPrice));
      addItem(li.name, li.qty || li.quantity, amount, p.evidenceType || '매입메모');
    }
  }

  for (const k of Object.keys(out)) {
    out[k].qty = Math.round(out[k].qty * 100) / 100;
    out[k].cost = Math.round(out[k].cost);
  }
  return out;
}

// ── 매입 적정도 등급 (실제 / 적정 비율) ──
function classifyPurchase(actualCost, adequateCost) {
  if (adequateCost <= 0) return { tag: 'none', label: '평가 불가', color: '#9ca3af' };
  if (actualCost <= 0) return { tag: 'missing', label: '🚨 매입 없음', color: '#52525B' };
  const ratio = actualCost / adequateCost;
  if (ratio < 0.5) return { tag: 'severe-under', label: '🚨 매입 매우 부족', color: '#52525B' };
  if (ratio < 0.8) return { tag: 'under', label: '⚠️ 매입 부족', color: '#A1A1AA' };
  if (ratio > 1.5) return { tag: 'severe-over', label: '🚨 매입 매우 과다', color: '#52525B' };
  if (ratio > 1.2) return { tag: 'over', label: '⚠️ 매입 과다', color: '#A1A1AA' };
  return { tag: 'ok', label: '✅ 적정', color: '#18181B' };
}

// ── 변동률 상태 등급 ──
// ratio = actual / theoretical
//   <0.7 또는 >1.5 = 🚨 큰 편차
//   0.7~0.9 또는 1.1~1.5 = ⚠️ 약간 편차
//   0.9~1.1 = ✅ 적정
function classifyVariance(actualQty, theoreticalQty) {
  if (theoreticalQty <= 0 && actualQty <= 0) return { tag: 'none', label: '데이터 없음', color: '#9ca3af' };
  if (theoreticalQty <= 0) return { tag: 'extra', label: '레시피 없음', color: '#9ca3af' };
  if (actualQty <= 0) return { tag: 'missing', label: '매입 없음', color: '#A1A1AA' };
  const ratio = actualQty / theoreticalQty;
  if (ratio < 0.5) return { tag: 'severe-under', label: '🚨 매입 매우 부족', color: '#52525B' };
  if (ratio < 0.8) return { tag: 'under', label: '⚠️ 매입 부족', color: '#A1A1AA' };
  if (ratio > 2.0) return { tag: 'severe-over', label: '🚨 매입 매우 과다', color: '#52525B' };
  if (ratio > 1.3) return { tag: 'over', label: '⚠️ 매입 과다', color: '#A1A1AA' };
  return { tag: 'ok', label: '✅ 적정', color: '#18181B' };
}

// ── 종합 분석 ──
// 입력:
//   transactions: 거래장부
//   purchaseLog: 매입메모
//   categories: 레시피
//   prices: 단가표
//   inventory: { items: { name: { currentStock, ... } } }  — 현재 재고 (참고용)
//   monthKey: 'YYYY-MM'
//   inventoryDelta: { ingredientName: number }  — 재고 변화량 (기말 - 기초, optional)
// 출력: { rows: [...], totals: {...} }
export function analyzeVariance({
  transactions = [],
  purchaseLog = [],
  categories = [],
  prices = [],
  inventory = {},
  monthKey,
  inventoryDelta = {},
  menuRecipeMap = {},
} = {}) {
  if (!monthKey) return { rows: [], totals: {} };

  // 메뉴 판매 집계 (이번 달)
  const dateRange = { start: monthKey + '-01', end: monthKey + '-31' };
  const salesAgg = aggregateMenuSales(transactions, dateRange);

  // 이론 + 실제 계산
  const theoretical = calcTheoreticalUsage(salesAgg, categories, prices, menuRecipeMap);
  const menuMapping = theoretical.__menuMapping || [];
  delete theoretical.__menuMapping;
  const actual = calcActualPurchases(transactions, purchaseLog, monthKey, prices);

  // 모든 식재료 키 합집합
  const allKeys = new Set([...Object.keys(theoretical), ...Object.keys(actual)]);

  const invItems = (inventory && inventory.items) || {};

  const rows = [];
  for (const key of allKeys) {
    const t = theoretical[key];
    const a = actual[key];
    const name = (t?.name) || (a?.name) || key;
    const unit = (t?.unit) || (a?.unit) || '';
    const theoreticalQty = t ? t.qty : 0;
    const theoreticalCost = t ? t.cost : 0;
    const actualQty = a ? a.qty : 0;
    const actualCost = a ? a.cost : 0;

    // 재고 변화량 (사용자 입력) — 기말 - 기초가 음수면 사용된 것
    // 즉 실제 사용량 = 매입 - (기말 - 기초) = 매입 + (기초 - 기말)
    const delta = num(inventoryDelta[name]) || num(inventoryDelta[key]) || 0;
    // delta > 0 → 재고 증가 (매입의 일부가 재고로 남음, 사용량은 매입 - delta)
    // delta < 0 → 재고 감소 (재고를 더 썼다, 사용량은 매입 + |delta|)
    const estimatedUsageQty = actualQty - delta;
    const estimatedUsageCost = actualCost - delta * (t?.unitPrice || 0);

    // 편차 = (추정 사용) - 이론
    const varianceQty = estimatedUsageQty - theoreticalQty;
    const varianceCost = estimatedUsageCost - theoreticalCost;
    const variancePct = theoreticalQty > 0 ? (varianceQty / theoreticalQty) * 100 : null;

    const status = classifyVariance(estimatedUsageQty, theoreticalQty);

    const invEntry = invItems[name];
    const currentStock = invEntry ? num(invEntry.currentStock) : null;
    const safetyStock = invEntry ? num(invEntry.safetyStock) : 0;

    // 적정 매입 계산: 이론 사용량 + 안전재고 보충분
    const stockGap = (currentStock !== null) ? Math.max(0, safetyStock - currentStock) : 0;
    const unitPriceForRow = (t?.unitPrice) || 0;
    const replenishCost = stockGap * unitPriceForRow;
    const adequatePurchaseQty = theoreticalQty + stockGap;
    const adequatePurchaseCost = theoreticalCost + replenishCost;
    const purchaseVarianceCost = actualCost - adequatePurchaseCost;
    const purchaseStatus = classifyPurchase(actualCost, adequatePurchaseCost);

    rows.push({
      name,
      unit,
      theoreticalQty,
      theoreticalCost,
      actualQty,
      actualCost,
      delta,
      estimatedUsageQty: Math.round(estimatedUsageQty * 100) / 100,
      estimatedUsageCost: Math.round(estimatedUsageCost),
      varianceQty: Math.round(varianceQty * 100) / 100,
      varianceCost: Math.round(varianceCost),
      variancePct: variancePct !== null ? Math.round(variancePct * 10) / 10 : null,
      status,
      currentStock,
      safetyStock,
      stockGap: Math.round(stockGap * 100) / 100,
      adequatePurchaseQty: Math.round(adequatePurchaseQty * 100) / 100,
      adequatePurchaseCost: Math.round(adequatePurchaseCost),
      purchaseVarianceCost: Math.round(purchaseVarianceCost),
      purchaseStatus,
      matched: (t?.matched ?? false) || (a?.matched ?? false),
    });
  }

  // 편차 크기로 정렬 (가장 큰 문제부터)
  rows.sort((x, y) => Math.abs(y.varianceCost) - Math.abs(x.varianceCost));

  const totals = {
    theoreticalCost: rows.reduce((s, r) => s + r.theoreticalCost, 0),
    actualCost: rows.reduce((s, r) => s + r.actualCost, 0),
    varianceCost: rows.reduce((s, r) => s + r.varianceCost, 0),
    adequatePurchaseCost: rows.reduce((s, r) => s + r.adequatePurchaseCost, 0),
    replenishNeededCount: rows.filter(r => r.stockGap > 0).length,
    ingredientCount: rows.length,
    salesCount: Object.values(salesAgg).reduce((s, v) => s + (v?.qty || 0), 0),
    matchedMenuCount: Object.values(salesAgg).filter(s => {
      return (categories || []).some(c => (c.items || []).some(i => i.type === 'menu' && isMenuMatch(i.name, s.name)));
    }).length,
    totalMenuCount: Object.keys(salesAgg).length,
  };

  return { rows, totals, menuMapping };
}
